import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ReactECharts from 'echarts-for-react'
import { AppIcon } from '../ui/icon'
import { mdiCalendarExport, mdiContentCopy, mdiRefresh, mdiTrashCanOutline } from '@mdi/js'
import { apiClient } from '../../api/client'
import { useTheme } from '../../contexts/ThemeContext'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog'

interface Props {
    org: string
    assetUid: string
    // Initial lens (read once from the URL for bookmarkability).
    lens?: string
    // Persist the chosen lens back to the URL.
    onLensChange?: (lens: string) => void
}

const SCORE_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/engagement-score'
const CAL_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/calendar'
const CAL_TOKEN_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/calendar/token'

type Tone = 'success' | 'error'
interface Notice { tone: Tone, message: string }

// localized reads a {fr,en} label map for the active language.
function localized(map: Record<string, string> | undefined, lang: string): string {
    if (!map) return ''
    return map[lang] || map.fr || map.en || ''
}

// ECharts paints on a canvas and never resolves CSS custom properties, so a
// `var(--muted)` handed to it renders black and a `var(--foreground)` label
// vanishes. Resolve the token against the document — which carries the active
// theme's values — and fall back to a literal when it is not defined.
function token(name: string, fallback: string): string {
    if (typeof window === 'undefined') return fallback
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return v || fallback
}

// gaugeColor maps a 0–100 score to the compliance palette (ko/due/ok).
function gaugeColor(score: number): string {
    if (score >= 80) return token('--chart-2', '#16a34a')
    if (score >= 50) return token('--chart-4', '#d97706')
    return token('--destructive', '#dc2626')
}

export function EngagementScore({ org, assetUid, lens, onLensChange }: Props) {
    const { t, i18n } = useTranslation('engagement')
    // Read purely so a theme switch re-renders and the canvas tokens above are
    // resolved again against the new palette.
    useTheme()
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en'
    const queryClient = useQueryClient()
    const [notice, setNotice] = useState<Notice | null>(null)
    const [confirm, setConfirm] = useState<null | 'rotate' | 'revoke'>(null)

    const notify = useCallback((tone: Tone, message: string) => setNotice({ tone, message }), [])

    // The selected lens lives in local state (seeded from the URL) so switching
    // is instant; the parent mirrors it to the URL for bookmarkability.
    const [selectedLens, setSelectedLens] = useState<string | undefined>(lens)
    const selectLens = useCallback((code: string) => {
        setSelectedLens(code)
        onLensChange?.(code)
    }, [onLensChange])

    const { data: score, isLoading } = useQuery({
        queryKey: ['engagement-score', org, assetUid],
        queryFn: async () => {
            const { data, error } = await apiClient.GET(SCORE_PATH, {
                params: { path: { orgCode: org, assetUid } },
            })
            if (error) throw error
            return data
        },
    })

    const { data: calendar } = useQuery({
        queryKey: ['engagement-calendar', org, assetUid],
        queryFn: async () => {
            const { data, error } = await apiClient.GET(CAL_PATH, {
                params: { path: { orgCode: org, assetUid } },
            })
            if (error) throw error
            return data
        },
    })

    const refreshCalendar = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['engagement-calendar', org, assetUid] })
    }, [queryClient, org, assetUid])

    const rotateMutation = useMutation({
        mutationFn: async () => {
            const { data, error } = await apiClient.POST(CAL_TOKEN_PATH, {
                params: { path: { orgCode: org, assetUid } },
            })
            if (error) throw error
            return data
        },
        onSuccess: () => { refreshCalendar(); setConfirm(null) },
        onError: () => { setConfirm(null); notify('error', t('error')) },
    })

    const revokeMutation = useMutation({
        mutationFn: async () => {
            const { error } = await apiClient.DELETE(CAL_TOKEN_PATH, {
                params: { path: { orgCode: org, assetUid } },
            })
            if (error) throw error
        },
        onSuccess: () => { refreshCalendar(); setConfirm(null); notify('success', t('calendar.revoked')) },
        onError: () => { setConfirm(null); notify('error', t('error')) },
    })

    const activeLens = useMemo(() => {
        const lenses = score?.lenses ?? []
        if (selectedLens && lenses.some((l: any) => l.code === selectedLens)) return selectedLens
        return lenses[0]?.code
    }, [score, selectedLens])

    const currentLens = useMemo(
        () => (score?.lenses ?? []).find((l: any) => l.code === activeLens),
        [score, activeLens],
    )

    if (isLoading) {
        return (
            <div className="space-y-4" data-testid="engagement-loading">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-24 w-full" />
            </div>
        )
    }
    if (!score) {
        return <p className="text-sm text-muted-foreground" data-testid="engagement-error">{t('error')}</p>
    }

    const gaugeOption = {
        series: [{
            type: 'gauge',
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: 100,
            progress: { show: true, width: 14, itemStyle: { color: gaugeColor(score.overall ?? 0) } },
            axisLine: { lineStyle: { width: 14, color: [[1, token('--muted', '#e5e7eb')]] } },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            pointer: { show: false },
            anchor: { show: false },
            detail: {
                valueAnimation: true,
                fontSize: 40,
                fontWeight: 700,
                offsetCenter: [0, '0%'],
                formatter: '{value}',
                color: token('--foreground', '#0a0a0a'),
            },
            data: [{ value: score.overall ?? 0 }],
        }],
    }

    return (
        <div className="space-y-6" data-testid="engagement-score">
            <div>
                <h3 className="text-lg font-semibold text-foreground">{t('title')}</h3>
                <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
            </div>

            {notice && (
                <div
                    role="status"
                    data-testid="engagement-notice"
                    className={`rounded-md border px-3 py-2 text-sm ${notice.tone === 'error'
                        ? 'border-destructive/40 bg-destructive/10 text-destructive'
                        : 'border-border bg-muted text-foreground'}`}
                >
                    {notice.message}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Radial gauge */}
                <div className="bg-card rounded-lg border border-border p-4" data-testid="engagement-gauge">
                    <div className="text-sm text-muted-foreground text-center">{t('overall')}</div>
                    <ReactECharts option={gaugeOption} style={{ height: '220px', width: '100%' }} opts={{ renderer: 'canvas' }} />
                </div>

                {/* Category bars */}
                <div className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid="engagement-categories">
                    <h4 className="font-medium text-foreground">{t('categories_title')}</h4>
                    {(score.categories ?? []).map((c: any) => (
                        <div key={c.code} data-testid={`engagement-category-${c.code}`}>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-foreground">{localized(c.name, lang)}</span>
                                <span className="text-muted-foreground tabular-nums">{c.score}</span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full rounded-full"
                                    style={{ width: `${c.score}%`, backgroundColor: gaugeColor(c.score) }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lens selector + ranked missing items */}
            <div className="bg-card rounded-lg border border-border p-4 space-y-4" data-testid="engagement-lenses">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground mr-1">{t('lens.title')}:</span>
                    {(score.lenses ?? []).map((l: any) => (
                        <Button
                            key={l.code}
                            variant={l.code === activeLens ? 'default' : 'outline'}
                            size="sm"
                            data-testid={`engagement-lens-${l.code}`}
                            onClick={() => selectLens(l.code)}
                        >
                            {localized(l.name, lang)} · {l.score}
                        </Button>
                    ))}
                </div>

                <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">{t('missing.title')}</h4>
                    {currentLens && currentLens.missing && currentLens.missing.length > 0 ? (
                        <ul className="divide-y divide-border" data-testid="engagement-missing-list">
                            {currentLens.missing.map((m: any, idx: number) => (
                                <li key={`${m.document_type_code}-${idx}`} className="py-2 flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm text-foreground font-medium">{m.document_type_code}</div>
                                        {m.hint && (
                                            <div className="text-xs text-muted-foreground">{localized(m.hint, lang)}</div>
                                        )}
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${m.status === 'expired'
                                        ? 'bg-[color-mix(in_srgb,var(--chart-4,#d97706)_20%,transparent)] text-foreground'
                                        : 'bg-destructive/10 text-destructive'}`}>
                                        {m.status === 'expired' ? t('missing.expired') : t('missing.absent')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground" data-testid="engagement-missing-none">{t('missing.none')}</p>
                    )}
                </div>
            </div>

            {/* Calendar subscribe card */}
            <div className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid="engagement-calendar">
                <div className="flex items-center gap-2">
                    <AppIcon tone="muted" path={mdiCalendarExport} size="md" />
                    <h4 className="font-medium text-foreground">{t('calendar.title')}</h4>
                </div>
                <p className="text-sm text-muted-foreground">{t('calendar.description')}</p>
                {calendar && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="default"
                            size="sm"
                            data-testid="engagement-calendar-subscribe"
                            onClick={() => { window.location.href = calendar.webcal_url as string }}
                        >
                            <AppIcon path={mdiCalendarExport} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('calendar.subscribe')}</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            data-testid="engagement-calendar-copy"
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(calendar.ics_url as string)
                                    notify('success', t('calendar.copied'))
                                } catch { notify('error', t('error')) }
                            }}
                        >
                            <AppIcon path={mdiContentCopy} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('calendar.copy')}</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            data-testid="engagement-calendar-rotate"
                            onClick={() => setConfirm('rotate')}
                        >
                            <AppIcon path={mdiRefresh} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('calendar.rotate')}</span>
                        </Button>
                        <Button
                            variant="destructive"
                            size="sm"
                            data-testid="engagement-calendar-revoke"
                            onClick={() => setConfirm('revoke')}
                        >
                            <AppIcon path={mdiTrashCanOutline} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('calendar.revoke')}</span>
                        </Button>
                    </div>
                )}
            </div>

            <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null) }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirm === 'revoke' ? t('calendar.revoke_confirm_title') : t('calendar.rotate_confirm_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirm === 'revoke' ? t('calendar.revoke_confirm_body') : t('calendar.rotate_confirm_body')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="engagement-calendar-confirm"
                            onClick={() => { if (confirm === 'revoke') revokeMutation.mutate(); else rotateMutation.mutate() }}
                        >
                            {confirm === 'revoke' ? t('calendar.revoke_confirm_action') : t('calendar.rotate_confirm_action')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
